import React, { useState, useRef } from "react";
import ProfilesTableLimits from "./ProfilesTableLimits";
import CategoryTableLimits from "./CategoryTableLimits";
import LimitSetTable from "./LimitSetTable";
import LimitSetHeader from "./LimitSetHeader";
import WagerTypesLimitTable from "./WagerTypesLimitTable";
import { errorHandler } from "../ErrorHandler";
import { successMsg } from "../SuccessHandler";
import CloneLimitSetModal from "./CloneLimitSetModal";
import LastChangesModal from "../lastChanges/LastChangesModal";
import WagerTypesConstants from "../../constants/WagerTypesConstants";
import ErrorCodes from "../../constants/ErrorCodes";
import SiteConfig from "../../constants/SiteConfig";
import { useDeleteFunctions, useInsertFunctions } from "../../functions";
import ConfirmDialog from "../utilities/ConfirmDialog";
import Rights from "../../constants/Rights";
import NewLimitSetModal from "./NewLimitSetModal";

const LimitsModal = ({
  data,
  profiles,
  trackCategory,
  limitSetList,
  idLimitSet,
  setIdLimitSet,
  limitSetBetTypes,
  setLimitSetBetTypes,
  setProfileOptionSelected,
  profileOptionSelected,
  setCategoryOptionSelected,
  categoryOptionSelected,
  limitSetInfo,
  setLimitSetInfo,
  getProfiles,
  getTrackCategory,
  getLimitSetList,
  getLimitSetID,
  betTypeOptionSelected,
  setBetTypeOptionSelected,
  lastChanges,
  setLastChanges,
  lastChangesInfo,
  setLastChangesInfo,
  fieldChangesInfo,
  setFieldChangesInfo,
  getFieldChanges,
  openLastChanges,
  profileName,
  setProfileName,
  categoryName,
  setCategoryName,
  betTypeName,
  setBetTypeName,
  setSessionNoLongerExists,
  publicIP,
}) => {
  const [filterInput, setFilterInput] = useState(""); //Profile filter
  const [filterInputCategory, setFilterInputCategory] = useState("");
  const [filterInputLimitSet, setFilterInputLimitSet] = useState("");
  const closeButtonRef = useRef();
  const tableRef = useRef(null);
  const divRef = useRef(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { deleteLimitSet } = useDeleteFunctions();
  const { insertLimitSet } = useInsertFunctions();
  const userRightsList = JSON.parse(localStorage.getItem("UserRights")) || [];

  let btnLimitSetChanges, btnBetTypeChanges;

  // Insert LimitSet Function
  const handleInsertLimitSet = async () => {
    const _errors = {};

    if (Object.keys(_errors).length === 0) {
      let LimitSetBetType = [...limitSetBetTypes];

      const limit = { ...limitSetInfo, LimitSetBetType };

      const res = await insertLimitSet(
        {
          ProfileID: profileOptionSelected,
          IDTrackCategory: categoryOptionSelected,
          LimitSetID: idLimitSet,
          PhoneLine: SiteConfig.LOGIN_PHONE_LINE,
          IP: publicIP,
          Limit: limit,
        },
        data.LogonSessionID,
      );

      if (res?.error) {
        if (
          res.Result === ErrorCodes.InvalidSession ||
          res.Result === ErrorCodes.SessionNoLongerExists
        ) {
          setSessionNoLongerExists(true);
        }
        return;
      } else {
        successMsg("Limit Set Saved");
        setHasChanges(false);
        getLimitSetList();
      }
    }
  };

  // This function is executed when closing the modal from the close button or from x
  const closeModal = () => {
    const cleanModal = () => {
      setLimitSetBetTypes([]);
      setLimitSetInfo({
        LimitSetName: "",
        ParlayMaxSize: "",
        MaxRaceWin: "",
        MaxRaceWinType: 0,
      });
      setBetTypeOptionSelected(0);
      setFilterInput("");
      setFilterInputCategory("");
      setFilterInputLimitSet("");
      setIdLimitSet("");
    };
    if (hasChanges) {
      ConfirmDialog(
        `Do you want to save the changes?`,
        "Exit anyway",
        "Yes, save it",
      ).then(async (result) => {
        if (result.isConfirmed) {
          handleInsertLimitSet();
          closeButtonRef.current.click();
          const div = divRef.current;
          if (div) {
            div.scrollTop = 0;
          }
          cleanModal();
        } else {
          closeButtonRef.current.click();
          const div = divRef.current;
          if (div) {
            div.scrollTop = 0;
          }
          cleanModal();
          setHasChanges(false);
        }
      });
    } else {
      closeButtonRef.current.click();
      const div = divRef.current;
      if (div) {
        div.scrollTop = 0;
      }
      cleanModal();
      setHasChanges(false);
    }
  };

  // This function is executed when user clicks on the Refresh button
  const refreshLimitSetInformation = () => {
    getProfiles();
    getTrackCategory();
    getLimitSetList();
    getLimitSetID();
    setBetTypeOptionSelected(0);
  };

  // This function is executed when user clicks on the tab LimitSet Changes button
  const openLimitSetChanges = () => {
    if (idLimitSet === "") {
      errorHandler("Select a LimitSet");
      return;
    }

    openLastChanges("LIMITSETLOG", idLimitSet, 0, limitSetInfo.LimitSetName);
    setIsModalOpen(true);
  };

  // This function is executed when user clicks on the tab BetType Changes button
  const openBetTypeChanges = () => {
    if (idLimitSet === "") {
      errorHandler("Select a LimitSet");
      return;
    }

    WagerTypesConstants.forEach((wt) => {
      if (parseInt(betTypeOptionSelected) === wt.id) {
        setBetTypeName(wt.name);
      }
    });

    openLastChanges(
      "LIMITSETBETTYPELOG",
      idLimitSet,
      betTypeOptionSelected,
      limitSetInfo.LimitSetName,
      betTypeName,
    );
    setIsModalOpen(true);
  };

  // This if allows you to manage the LimitSet Changes and BetType Changes buttons to display the modal only if a LimitSet is selected.
  btnBetTypeChanges = (
    <button type="button" className="btn btn-dark" onClick={openBetTypeChanges}>
      BetType Changes
    </button>
  );
  btnLimitSetChanges = (
    <button
      type="button"
      className="btn btn-dark"
      onClick={openLimitSetChanges}
    >
      LimitSet Changes
    </button>
  );

  let limitSetName;
  limitSetList.forEach((limitSet) => {
    if (parseInt(idLimitSet) === limitSet.LimitSetID) {
      limitSetName = limitSet.LimitSetName;
    }
  });

  const handleDeleteLimitSet = () => {
    if (idLimitSet === "") {
      errorHandler("Select a LimitSet");
      return;
    }
    ConfirmDialog(
      `Do you want to delete the <strong>${limitSetName}</strong> Limit?`,
    ).then(async (result) => {
      if (result.isConfirmed) {
        const res = await deleteLimitSet(
          {
            DeleteID: idLimitSet,
            PhoneLine: SiteConfig.LOGIN_PHONE_LINE,
            IP: publicIP,
          },
          data,
        );

        if (res?.error) {
          if (
            res.Result === ErrorCodes.InvalidSession ||
            res.Result === ErrorCodes.SessionNoLongerExists
          ) {
            setSessionNoLongerExists(true);
          }
          return;
        } else {
          successMsg("Limit Set Deleted Successfully");
          setLimitSetBetTypes([]);
          setIdLimitSet("");
          limitSetList.forEach((data, i) => {
            if (data.LimitSetID === idLimitSet) {
              limitSetList.splice(i, 1);
            }
          });
          setLimitSetInfo({
            LimitSetName: "",
            ParlayMaxSize: "",
            MaxRaceWin: "",
            MaxRaceWinType: 0,
          });
        }
      }
    });
  };

  return (
    <>
      <div
        className="modal fade"
        id="limitsModal"
        role="dialog"
        aria-hidden="false"
        data-keyboard="false"
        data-backdrop="static"
      >
        <div className="modal-dialog modal-xl">
          <div className="modal-content">
            <div className="modal-header headerModals">
              <h5
                className="modal-title"
                id="limitsModal"
                style={{ cursor: "default" }}
              >
                Limits
              </h5>
              <span
                data-dismiss="modal"
                ref={closeButtonRef}
                style={{ cursor: "inherit" }}
              ></span>
              <button type="button" className="close" onClick={closeModal}>
                &times;
              </button>
            </div>
            <div className="modal-body">
              <div className="row">
                <div className="col-sm-4">
                  <div className="row">
                    <div className="col-sm-4" style={{ padding: "5px" }}>
                      <ProfilesTableLimits
                        profiles={profiles}
                        filterInput={filterInput}
                        setFilterInput={setFilterInput}
                        setProfileOptionSelected={setProfileOptionSelected}
                        profileOptionSelected={profileOptionSelected}
                        setProfileName={setProfileName}
                        setBetTypeOptionSelected={setBetTypeOptionSelected}
                        setBetTypeName={setBetTypeName}
                      />
                    </div>
                    <div className="col-sm-4" style={{ padding: "5px" }}>
                      <CategoryTableLimits
                        trackCategory={trackCategory}
                        filterInputCategory={filterInputCategory}
                        setFilterInputCategory={setFilterInputCategory}
                        setCategoryOptionSelected={setCategoryOptionSelected}
                        categoryOptionSelected={categoryOptionSelected}
                        setCategoryName={setCategoryName}
                        setBetTypeOptionSelected={setBetTypeOptionSelected}
                        setBetTypeName={setBetTypeName}
                      />
                    </div>
                    <div className="col-sm-4" style={{ padding: "5px" }}>
                      <LimitSetTable
                        limitSetList={limitSetList}
                        filterInputLimitSet={filterInputLimitSet}
                        setFilterInputLimitSet={setFilterInputLimitSet}
                        idLimitSet={idLimitSet}
                        setIdLimitSet={setIdLimitSet}
                        setBetTypeOptionSelected={setBetTypeOptionSelected}
                        setBetTypeName={setBetTypeName}
                      />
                    </div>
                  </div>
                </div>
                <div className="col-sm-8">
                  <div style={{ boxShadow: "-10px 5px 10px #d9e6f2" }}>
                    {limitSetInfo && (
                      <LimitSetHeader
                        limitSetInfo={limitSetInfo}
                        setLimitSetInfo={setLimitSetInfo}
                        setHasChanges={setHasChanges}
                      />
                    )}
                    <WagerTypesLimitTable
                      limitSetBetTypes={limitSetBetTypes}
                      divRef={divRef}
                      tableRef={tableRef}
                      setLimitSetBetTypes={setLimitSetBetTypes}
                      setHasChanges={setHasChanges}
                      betTypeOptionSelected={betTypeOptionSelected}
                      setBetTypeOptionSelected={setBetTypeOptionSelected}
                      setBetTypeName={setBetTypeName}
                    />
                  </div>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <div className="col-sm-8">
                <button
                  type="button"
                  className="btn btn-info"
                  onClick={refreshLimitSetInformation}
                >
                  Refresh
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  data-toggle="modal"
                  data-target="#newLimitSetModal"
                >
                  New LimitSet
                </button>
                <button
                  type="button"
                  className="btn btn-success"
                  data-toggle="modal"
                  data-target="#cloneLimitSetModal"
                >
                  Clone LimitSet
                </button>
                <button
                  type="button"
                  className="btn btn-danger"
                  onClick={() => handleDeleteLimitSet()}
                >
                  Delete LimitSet
                </button>
                {userRightsList.find(
                  (right) => right.FormName === Rights.FRM_LAST_CHANGES,
                ) && (
                  <button
                    type="button"
                    className="btn btn-dark"
                    onClick={() => {
                      openLastChanges(
                        "LIMITSETTRACKCATEGORYLOG",
                        profileOptionSelected,
                        categoryOptionSelected,
                        profileName,
                        categoryName,
                      );
                      setIsModalOpen(true);
                    }}
                  >
                    Category Changes
                  </button>
                )}

                {userRightsList.find(
                  (right) => right.FormName === Rights.FRM_LAST_CHANGES,
                ) && btnLimitSetChanges}
                {userRightsList.find(
                  (right) => right.FormName === Rights.FRM_LAST_CHANGES,
                ) && btnBetTypeChanges}
              </div>
              <div
                className="col-sm-4"
                style={{ textAlign: "right", paddingRight: "0px" }}
              >
                <button
                  type="button"
                  className="btn btn-success"
                  onClick={() => handleInsertLimitSet()}
                >
                  Save
                </button>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={closeModal}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
      <CloneLimitSetModal
        data={data}
        idLimitSet={idLimitSet}
        limitSetList={limitSetList}
        getLimitSetList={getLimitSetList}
        publicIP={publicIP}
        setSessionNoLongerExists={setSessionNoLongerExists}
      />
      <NewLimitSetModal
        data={data}
        getLimitSetList={getLimitSetList}
        setSessionNoLongerExists={setSessionNoLongerExists}
      />
      {isModalOpen && (
        <LastChangesModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          lastChanges={lastChanges}
          setLastChanges={setLastChanges}
          lastChangesInfo={lastChangesInfo}
          getFieldChanges={getFieldChanges}
          fieldChangesInfo={fieldChangesInfo}
          setLastChangesInfo={setLastChangesInfo}
          setFieldChangesInfo={setFieldChangesInfo}
        />
      )}
    </>
  );
};

export default LimitsModal;
